import "./CompensationHeader.css";

/**
 * CompensationHeader
 *
 * Single header shared by both the role-list table view and the Cost Band
 * Detail view - matches old Minerva's core-compensation.component.html,
 * where the same heading conditionally shows role/jobCode/pills depending
 * on whether a role is selected yet. Restyled to match the new prototype's
 * "Fully inclusive annual compensation cost, across territories." hero.
 *
 * Props:
 *  - roleName: string | null   (job title name once a role is picked; null in role-list state)
 *  - jobCode: string | null    (from GetCompensationOverview payload - real, not hardcoded)
 *  - alternateTitles: string[] (from source_job_title_alternative.split(';'))
 *  - quarterLabel: string      (selected quarter, including the filter's default)
 */
export default function CompensationHeader({
  roleName = null,
  jobCode = null,
  alternateTitles = [],
  quarterLabel = "",
}) {
  const hasRole = !!roleName;

  return (
    <div className={`comp-header${hasRole ? " comp-header-compact" : ""}`}>
      {/* Decorative leading dash removed (18-Aug: reads as an em dash). */}
      <div className="comp-header-tag">
        COMPENSATION INTELLIGENCE{quarterLabel && <> &middot; {quarterLabel}</>}
      </div>

      <h1 className="comp-header-title">
        Fully inclusive annual compensation cost, <em>across territories.</em>
      </h1>

      {hasRole && (
        <div className="comp-header-meta">
          <span className="comp-header-meta-item">
            <span className="comp-header-meta-label">ROLE</span>
            <span className="comp-header-meta-value">{roleName}</span>
          </span>
          <span className="comp-header-meta-sep">&middot;</span>
          <span className="comp-header-meta-item">
            <span className="comp-header-meta-label">JOB CODE</span>
            <span className="comp-header-meta-value">{jobCode || "-"}</span>
          </span>
          <span className="comp-header-meta-sep">&middot;</span>
          {/* Currency/Basis: source table not confirmed yet, hardcoded per instruction */}
          <span className="comp-header-meta-item">
            <span className="comp-header-meta-label">CURRENCY</span>
            <span className="comp-header-meta-value">GBP &pound;</span>
          </span>
          <span className="comp-header-meta-sep">&middot;</span>
          <span className="comp-header-meta-item">
            <span className="comp-header-meta-label">BASIS</span>
            <span className="comp-header-meta-value">Fully loaded</span>
          </span>
        </div>
      )}

      {/* Marketing-style blurb only makes sense before a role is picked -
          once you're looking at real data it's just dead space pushing the
          table further down, which is exactly what was reported. */}
      {!hasRole && (
        <p className="comp-header-desc">
          A comparative view of total annual employer cost (base salary,
          statutory contributions, and benefits) for the same role profile,
          set against the three internal experience tiers used in pay decisions.
        </p>
      )}

      {hasRole && alternateTitles.length > 0 && alternateTitles[0] !== "" && (
        <div className="comp-header-pills">
          {alternateTitles.map((title) => (
            <span key={title} className="comp-header-pill">{title.trim()}</span>
          ))}
        </div>
      )}
    </div>
  );
}
